<template>
  <v-dialog
    :model-value="visible"
    @update:model-value="handleDialogUpdate"
    width="auto"
    :fullscreen="size === 'fullscreen'"
    :attach="true"
    class="modal-overlay"
  >
    <div
      class="modal-content"
      :class="[`modal-${size}`, `modal-position-${position}`]"
    >
      <div class="modal-header">
        <h3 class="modal-title">{{ title }}</h3>
        <button class="modal-close" @click="handleCancel">×</button>
      </div>
      <div class="modal-body">
        <slot></slot>
      </div>
      <div class="modal-footer" v-if="$slots.footer">
        <slot name="footer"></slot>
      </div>
      <div class="modal-footer" v-else>
        <BaseButton
          class="btn btn-cancel"
          @click="handleCancel"
          :loading="loading"
          loading-text="Loading..."
        >
          {{ cancelText }}
        </BaseButton>
        <BaseButton
          class="btn btn-primary"
          @click="handleOk"
          :loading="loading"
          loading-text="Loading..."
        >
          {{ okText }}
        </BaseButton>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import BaseButton from "@/components/common/BaseButton.vue";

export default {
  name: "ModalComponent",
  components: {
    BaseButton,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: "Modal Title",
    },
    size: {
      type: String,
      default: "medium",
      validator: (value) =>
        ["small", "medium", "large", "fullscreen"].includes(value),
    },
    position: {
      type: String,
      default: "center",
      validator: (value) =>
        ["center", "top", "right", "bottom", "left"].includes(value),
    },
    okText: {
      type: String,
      default: "OK",
    },
    cancelText: {
      type: String,
      default: "Cancel",
    },
  },
  emits: ["ok", "cancel"],
  methods: {
    handleOk() {
      this.$emit("ok");
    },
    handleCancel() {
      this.$emit("cancel");
    },
    handleDialogUpdate(value) {
      if (!value) {
        this.handleCancel();
      }
    },
  },
};
</script>

<style scoped>
/* Match original scrim color (rgba(0, 0, 0, 0.5)) */
.modal-overlay :deep(.v-overlay__scrim) {
  background: black;
  opacity: 0.5;
}

.modal-overlay :deep(.v-overlay__content) {
  max-height: 90vh;
  margin: 0;
}

.modal-content {
  background-color: white;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

/* Sizes */
.modal-small {
  width: 400px;
}

.modal-medium {
  width: 600px;
}

.modal-large {
  width: 800px;
}

.modal-fullscreen {
  width: 95vw;
  height: 95vh;
}

/* Positions */
.modal-position-top {
  margin-top: 50px;
  align-self: flex-start;
}

.modal-position-right {
  margin-right: 50px;
  align-self: flex-end;
}

.modal-position-bottom {
  margin-bottom: 50px;
  align-self: flex-end;
}

.modal-position-left {
  margin-left: 50px;
  align-self: flex-start;
}

.modal-header {
  padding: 16px 24px;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-title {
  margin: 0;
  color: rgba(0, 0, 0, 0.85);
  font-weight: 500;
  font-size: var(--fs-medium);
}

.modal-close {
  background: transparent;
  border: none;
  font-size: var(--fs-big);
  cursor: pointer;
  color: rgba(0, 0, 0, 0.45);
}

.modal-body {
  padding: 24px;
  overflow-y: auto;
  flex: 1;
}

.modal-footer {
  padding: 10px 16px;
  border-top: 1px solid #f0f0f0;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.btn {
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: var(--fs-small);
  transition: all 0.3s;
}

.btn-cancel {
  background-color: white;
  border: 1px solid #d9d9d9;
  color: rgba(0, 0, 0, 0.65);
}

.btn-cancel:hover {
  color: #ff4040;
  border-color: #ff4040;
}

.btn-primary {
  background-color: #00925e;
  border: 1px solid #00925e;
  color: white;
}

.btn-primary:hover {
  background-color: #02764d;
  border-color: #02764d;
}
</style>
